import React from 'react';
import SearchBar from './SearchBar';
import ConfirmationDialog from './ConfirmationDialog';
import {FaEdit, FaTrash} from 'react-icons/fa';
import {HiArrowLeft, HiCheck, HiX} from 'react-icons/hi';
import { FiCalendar } from 'react-icons/fi';
// @ts-ignore
import DatePicker from 'react-datepicker';
import {useSelector} from 'react-redux';
import {useJwt} from '../../../helpers/common';
import { getOrgWorkingDays, defaultWorkingDays } from '../../../utils/helperUtils';

type Milestone = {
  _uid: string;
  name: string;
  color: string;
  start_date: any;
};

interface MilestoneManagement {
  milestonesState: Milestone[];
  isAddingMilestone: boolean;
  setIsAddingMilestone: (v: boolean) => void;
  addMilestone: Milestone;
  setAddMilestone: (m: Milestone) => void;
  editMilestone: Milestone | null;
  setEditMilestone: (m: Milestone | null) => void;
  handleAddMilestone: () => Promise<void> | void;
  handleUpdateMilestone: () => Promise<void> | void;
  handleDeleteMilestone: (uid: string) => Promise<void> | void;
  isMilestoneActionLoading: boolean;
}

interface Props {
  milestoneManagement: MilestoneManagement;
  singleRoadmapLoading: boolean;
  minDate: string;
  maxDate: string;
}

const MilestonesPanel: React.FC<Props> = ({ milestoneManagement, singleRoadmapLoading, minDate, maxDate }) => {
  const [milestoneSearch, setMilestoneSearch] = React.useState('');
  const [deleteConfirmation, setDeleteConfirmation] = React.useState<{
    show: boolean;
    uid: string;
    name: string;
    position?: { top: number; left: number };
  }>({ show: false, uid: '', name: '' });

  const { org_id = '' } = useJwt() as any;
  const {
    orgSchedule: organizationHolidays = []
  } = useSelector((state: any) => state.organization || {});

  const organizationWorkingDays = getOrgWorkingDays(org_id || '');

  // Build a fast lookup set of holiday dates in YYYY-MM-DD
  const holidayDateSet = React.useMemo(() => {
    const set = new Set<string>();
    try {
      organizationHolidays?.forEach((h: any) => {
        const raw = (h?.date ?? '').toString();
        if (!raw) return;
        const ymd = raw.includes('T') ? raw.split('T')[0] : raw;
        if (ymd) set.add(ymd);
      });
    } catch {}
    return set;
  }, [organizationHolidays]);

  const isDateSelectable = React.useCallback((date: Date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) return false;
    const jsDay = date.getDay(); // 0 = Sunday ... 6 = Saturday

    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const ymd = `${y}-${m}-${d}`;

    const isHoliday = holidayDateSet.has(ymd);
    const hasConfiguredWorkingDays = Array.isArray(organizationWorkingDays) && organizationWorkingDays.length > 0;
    const workingDays = hasConfiguredWorkingDays ? organizationWorkingDays : defaultWorkingDays;
    const isWorkingDay = Array.isArray(workingDays) && workingDays.includes(jsDay);

    return isWorkingDay && !isHoliday;
  }, [holidayDateSet, organizationWorkingDays]);

  const handleDeleteWithConfirmation = (uid: string, name: string, event: React.MouseEvent) => {
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    setDeleteConfirmation({ show: true, uid, name, position: { top: rect.top, left: rect.left - 200 } });
  };

  const SkeletonCard = ({ keyId }: { keyId: number }) => (
    <div key={keyId} className="mb-3 rounded-xl border border-light-border-default dark:border-slate-700 bg-light-surfacePrimary dark:bg-secondary-dark shadow-sm overflow-hidden animate-pulse">
      <div className="flex items-center px-5 pb-1 pt-3">
        <span className="w-2 h-2 rounded-full mr-3 bg-light-surfaceSecondary dark:bg-gray-600" />
        <div className="flex-1 h-4 rounded bg-light-surfaceSecondary dark:bg-gray-600" />
        <div className="ml-2 flex items-center gap-1">
          <div className="w-6 h-6 rounded bg-light-surfaceSecondary dark:bg-gray-600" />
          <div className="w-6 h-6 rounded bg-light-surfaceSecondary dark:bg-gray-600" />
        </div>
      </div>
      <div className="px-5 py-3 bg-light-surfaceHover dark:bg-slate-800/50">
        <div className="flex items-center gap-4">
          <div className="h-3 w-24 rounded bg-light-surfaceSecondary dark:bg-gray-700" />
          <div className="h-3 w-16 rounded bg-light-surfaceSecondary dark:bg-gray-700" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="tab-container">
      <div className="tab-header">
        <div className="tab-search-wrapper">
          <SearchBar
            searchValue={milestoneSearch}
            onSearchChange={setMilestoneSearch}
            placeholder={`Search for milestones`}
            statusFilters={[]}
            setStatusFilters={() => {}}
            statusDropdownOpen={false}
            setStatusDropdownOpen={() => {}}
            showStatusFilter={false}
          />
        </div>
        <button
          className="tab-add-button"
          onClick={() => {
            milestoneManagement.setIsAddingMilestone(true);
            milestoneManagement.setAddMilestone({ _uid: '', name: '', color: '#000000', start_date: null });
          }}
        >
          + Add
        </button>
      </div>

      <div className="flex-1">
        {singleRoadmapLoading ? (
          <div className="px-3 mt-1 py-3">
            {[0,1,2,3].map((i) => (<SkeletonCard keyId={i} />))}
          </div>
        ) : (
          <>
            <div className="px-3 mt-1 py-3">
              {milestoneManagement.milestonesState.length > 0 && !milestoneManagement.isAddingMilestone && (
                milestoneManagement.milestonesState
                  .filter((ms: any) => (ms.name || '').toLowerCase().includes(milestoneSearch.toLowerCase()))
                  .map((ms: any) => {
                    const isEditing = milestoneManagement.editMilestone && milestoneManagement.editMilestone._uid === ms._uid;
                    return (
                      <div
                        key={ms._uid}
                        className={`mb-3 rounded-xl border border-light-border-default dark:border-slate-700 bg-light-surfacePrimary dark:bg-secondary-dark shadow-sm overflow-hidden transition-all duration-300 hover:bg-light-interactive-hover dark:hover:bg-slate-700 ${isEditing ? 'ring-2 ring-primary-light dark:ring-primary-dark scale-[1.01]' : ''} animate-fade-in`}
                        onClick={() => {
                          if (!isEditing) {
                            milestoneManagement.setIsAddingMilestone(false);
                            milestoneManagement.setEditMilestone({ ...ms });
                          }
                        }}
                      >
                        <div
                          className="flex items-center px-5 pb-1 pt-3"
                          onClick={(e) => {
                            if (isEditing) {
                              e.stopPropagation();
                              milestoneManagement.setEditMilestone(null);
                            }
                          }}
                        >
                          <span className="w-2 h-2 rounded-full mr-3" style={{ backgroundColor: (isEditing ? (milestoneManagement.editMilestone?.color || ms.color) : ms.color) || '#000000' }} />
                          <div className="flex-1 truncate  font-semibold" title={isEditing ? (milestoneManagement.editMilestone?.name || ms.name) : ms.name}>{isEditing ? (milestoneManagement.editMilestone?.name || ms.name) : (ms.name || 'Untitled Milestone')}</div>
                          <div className="ml-2 flex items-center gap-1">
                            <button
                              className="p-1 rounded-md hover:bg-light-interactive-hover dark:hover:bg-slate-700 transition-colors duration-200"
                              onClick={(e) => {
                                e.stopPropagation();
                                milestoneManagement.setIsAddingMilestone(false);
                                milestoneManagement.setEditMilestone({ ...ms });
                              }}
                              title="Edit milestone"
                            >
                              <FaEdit className="text-light-text-secondary dark:text-gray-400" size={15} />
                            </button>
                            <button
                              className="p-1 rounded-md hover:bg-light-interactive-hover dark:hover:bg-slate-700 transition-colors duration-200"
                              onClick={(e) => { e.stopPropagation(); handleDeleteWithConfirmation(ms._uid, ms.name, e); }}
                              title="Delete milestone"
                            >
                              <FaTrash className="text-light-text-secondary dark:text-gray-400" size={13} />
                            </button>
                          </div>
                        </div>

                        <div className="px-5 py-3 bg-light-surfaceHover dark:bg-slate-800/50 transition-all duration-200">
                          {isEditing ? (
                            <>
                              <div className="mb-3">
                                <div className="text-xs text-light-text-secondary dark:text-gray-400 mb-1 font-semibold">Name</div>
                                <input
                                  className="block w-full px-3 py-2 text-sm rounded-lg border border-light-border-default dark:border-slate-700 bg-light-surfacePrimary dark:bg-secondary-dark"
                                  value={milestoneManagement.editMilestone?.name || ''}
                                  onChange={(e) => milestoneManagement.setEditMilestone({ ...(milestoneManagement.editMilestone as any), name: e.target.value })}
                                  placeholder="Milestone name"
                                />
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end mb-3">
                                <div>
                                  <div className="text-xs text-light-text-secondary dark:text-gray-400 mb-1 font-semibold">Date</div>
                                  <div className="relative">
                                    <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-light-text-secondary dark:text-gray-300 z-10 pointer-events-none" size={16} />
                                    <DatePicker
                                      className="bg-light-surfaceSecondary border border-light-border-default text-light-text-primary text-sm focus:ring-2 focus:ring-primary-light focus:border-primary-light block w-full p-2.5 pl-9 dark:bg-gray-700 dark:border-hover-dark dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark rounded-lg"
                                      selected={(milestoneManagement.editMilestone?.start_date ? new Date(milestoneManagement.editMilestone.start_date) : null) as any}
                                      onChange={(date: any) => milestoneManagement.setEditMilestone({ ...(milestoneManagement.editMilestone as any), start_date: date })}
                                      autoComplete='off'
                                      filterDate={isDateSelectable}
                                      placeholderText="Select date"
                                      portalId="root"
                                      popperPlacement="bottom-start"
                                      popperContainer={({ children }: { children: React.ReactNode }) => (
                                        <div style={{ zIndex: 9999 }}>{children}</div>
                                      )}
                                    />
                                  </div>
                                </div>
                                <div>
                                  <div className="text-xs text-light-text-secondary dark:text-gray-400 mb-1 font-semibold">Color</div>
                                  <input className="w-full h-10 rounded" type="color" value={milestoneManagement.editMilestone?.color || '#000000'} onChange={(e) => milestoneManagement.setEditMilestone({ ...(milestoneManagement.editMilestone as any), color: e.target.value })} />
                                </div>
                              </div>
                              <div className="flex justify-end gap-2 mt-1">
                                <button
                                  className="px-3 py-1.5 rounded-lg bg-light-surfaceSecondary text-light-text-primary font-semibold hover:bg-light-interactive-hover dark:bg-slate-700 dark:text-gray-200 dark:hover:bg-slate-600 text-sm flex items-center gap-1 border border-light-border-default dark:border-slate-600 transition-colors duration-200"
                                  onClick={() => milestoneManagement.setEditMilestone(null)}
                                  disabled={milestoneManagement.isMilestoneActionLoading}
                                >
                                  <HiX size={16}/> Cancel
                                </button>
                                {(() => {
                                  const canSave = Boolean(milestoneManagement.editMilestone?.name && milestoneManagement.editMilestone?.start_date);
                                  return (
                                    <button
                                      className={`px-3 py-1.5 rounded-lg font-semibold text-sm flex items-center gap-1 transition-colors duration-200 ${
                                        !canSave
                                          ? 'bg-light-surfaceSecondary text-light-text-muted cursor-not-allowed border border-light-border-default'
                                          : 'bg-primary-light text-white hover:bg-primary-hover shadow-sm'
                                      }`}
                                      onClick={milestoneManagement.handleUpdateMilestone}
                                      disabled={!canSave || milestoneManagement.isMilestoneActionLoading}
                                    >
                                      <HiCheck size={16}/> Save Milestone
                                    </button>
                                  );
                                })()}
                              </div>
                            </>
                          ) : (
                            <div className="flex flex-wrap items-center gap-3 text-xs text-light-text-secondary dark:text-gray-300">
                              <div className="flex items-center gap-1">
                                <span className="font-semibold">Date:</span>
                                {ms?.start_date ? (
                                  <span>{new Date(ms.start_date).toLocaleDateString()}</span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-light-text-muted dark:text-gray-400">
                                    <FiCalendar size={14} />
                                    <span>Not set</span>
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1"><span className="font-semibold">Color:</span><span className="w-3.5 h-3.5 rounded inline-block" style={{ backgroundColor: ms.color || '#000000' }} /></div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
              )}
            </div>

            {/* Add form */}
            {milestoneManagement.isAddingMilestone && (
              <div className="px-3">
                <div className="rounded-xl border border-light-border-default dark:border-slate-700 bg-light-surfacePrimary dark:bg-secondary-dark p-3 shadow-sm w-full max-w-full">
                  {/* Header like dependency form */}
                  <div className="flex items-center gap-2 mb-3">
                    <button
                      onClick={() => milestoneManagement.setIsAddingMilestone(false)}
                      className="p-1.5 rounded-full hover:bg-light-interactive-hover dark:hover:bg-slate-700 text-light-text-secondary hover:text-light-text-primary transition-colors duration-200"
                      aria-label="Back"
                    >
                      <HiArrowLeft size={16} />
                    </button>
                    <h2 className="text-base font-bold text-light-text-primary dark:text-white">
                      Add Milestone
                    </h2>
                  </div>
                  <div className="mb-3">
                    <div className="text-xs text-light-text-secondary dark:text-gray-400 mb-1 font-semibold">Name</div>
                    <input
                      className="block w-full px-3 py-2 text-sm rounded-lg border border-light-border-default dark:border-slate-700 bg-light-surfacePrimary dark:bg-secondary-dark"
                      value={milestoneManagement.addMilestone.name}
                      onChange={(e) => milestoneManagement.setAddMilestone({ ...milestoneManagement.addMilestone, name: e.target.value })}
                      placeholder="Milestone name"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end mb-3">
                    <div>
                      <div className="text-xs text-light-text-secondary dark:text-gray-400 mb-1 font-semibold">Date</div>
                      <div className="relative">
                        <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-light-text-secondary dark:text-gray-300 z-10 pointer-events-none" size={16} />
                        <DatePicker
                          className="z-[100] bg-light-surfaceSecondary border border-light-border-default text-light-text-primary text-sm focus:ring-2 focus:ring-primary-light focus:border-primary-light block w-full p-2.5 pl-9 dark:bg-gray-700 dark:border-hover-dark dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark rounded-lg"
                          selected={(milestoneManagement.addMilestone.start_date ? new Date(milestoneManagement.addMilestone.start_date) : null) as any}
                          onChange={(date: any) => milestoneManagement.setAddMilestone({ ...milestoneManagement.addMilestone, start_date: date })}
                          autoComplete='off'
                          showYearDropdown={true}
                          filterDate={isDateSelectable}
                          placeholderText="Select date"
                          portalId="root"
                          minDate={new Date(minDate)}
                          maxDate={new Date(maxDate)}
                          popperPlacement="bottom-start"
                          popperContainer={({ children }: { children: React.ReactNode }) => (
                            <div style={{ zIndex: 9999 }}>{children}</div>
                          )}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-light-text-secondary dark:text-gray-400 mb-1 font-semibold">Color</div>
                      <input className="w-full h-10 rounded" type="color" value={milestoneManagement.addMilestone.color} onChange={(e) => milestoneManagement.setAddMilestone({ ...milestoneManagement.addMilestone, color: e.target.value })} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                  {(() => {
                      const canSave = Boolean(milestoneManagement.addMilestone.name && milestoneManagement.addMilestone.start_date);
                      return (
                        <button
                          className={`px-3 py-1.5 rounded-lg font-semibold text-sm flex items-center gap-1 transition-colors duration-200 ${
                            !canSave
                              ? 'bg-light-surfaceSecondary text-light-text-muted cursor-not-allowed border border-light-border-default'
                              : 'bg-primary-light text-white hover:bg-primary-hover shadow-sm'
                          }`}
                          onClick={milestoneManagement.handleAddMilestone}
                          disabled={!canSave || milestoneManagement.isMilestoneActionLoading}
                        >
                          <HiCheck size={16}/> Add Milestone
                        </button>
                      );
                    })()}
                    <button
                      className="px-3 py-1.5 rounded-lg bg-light-surfaceSecondary text-light-text-primary font-semibold hover:bg-light-interactive-hover dark:bg-slate-700 dark:text-gray-200 dark:hover:bg-slate-600 text-sm flex items-center gap-1 border border-light-border-default dark:border-slate-600 transition-colors duration-200"
                      onClick={() => milestoneManagement.setIsAddingMilestone(false)}
                    >
                      <HiX size={16}/> Cancel
                    </button>
                   
                  </div>
                </div>
              </div>
            )}

            {/* Delete confirmation */}
            <ConfirmationDialog
              isOpen={deleteConfirmation.show}
              type={'single'}
              position={deleteConfirmation.position}
              dependencyName={deleteConfirmation.name}
              entityLabel={'milestone'}
              isLoading={milestoneManagement.isMilestoneActionLoading}
              onConfirm={async () => {
                const uid = deleteConfirmation.uid;
                setDeleteConfirmation({ show: false, uid: '', name: '' });
                await milestoneManagement.handleDeleteMilestone(uid);
              }}
              onCancel={() => setDeleteConfirmation({ show: false, uid: '', name: '' })}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default MilestonesPanel;


